import React, { useState, useMemo, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useUserLeagues } from './hooks/queries/useUserLeagues';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Navigation } from './components/Navigation';
import { LeagueTabBar } from './components/LeagueTabBar';
import { PublicHeader } from './components/PublicHeader';
import { Footer } from './components/Footer';
import { AlertMessage } from '@/components/ui/alert-message';
import { queryClient } from './lib/queryClient';
import { InstallPrompt } from './components/design-system/InstallPrompt';
import { PWAUpdatePrompt } from './components/design-system/PWAUpdatePrompt';
import { OfflineBanner } from './components/design-system/OfflineBanner';

// Lazy load components
const LoginForm = lazy(() => import('./components/LoginForm').then(m => ({ default: m.LoginForm })));
// Prefetch LoginForm chunk — it's the critical path for unauthenticated users
import('./components/LoginForm');
const RegisterForm = lazy(() => import('./components/RegisterForm').then(m => ({ default: m.RegisterForm })));
const ResetPasswordForm = lazy(() => import('./components/ResetPasswordForm').then(m => ({ default: m.ResetPasswordForm })));
const LeagueList = lazy(() => import('./components/LeagueList').then(m => ({ default: m.LeagueList })));
const LeagueStandings = lazy(() => import('./components/LeagueStandingsPage').then(m => ({ default: m.LeagueStandings })));
const CreateLeagueForm = lazy(() => import('./components/CreateLeagueForm').then(m => ({ default: m.CreateLeagueForm })));
const JoinLeagueForm = lazy(() => import('./components/JoinLeagueForm').then(m => ({ default: m.JoinLeagueForm })));
const InviteLinkHandler = lazy(() => import('./components/InviteLinkHandler').then(m => ({ default: m.InviteLinkHandler })));
const EventsPage = lazy(() => import('./components/EventsPage').then(m => ({ default: m.EventsPage })));
const UpcomingRacesPage = lazy(() => import('./components/UpcomingRacesPage').then(m => ({ default: m.UpcomingRacesPage })));
const RaceResultsPage = lazy(() => import('./components/RaceResultsPage').then(m => ({ default: m.RaceResultsPage })));
const PredictionPage = lazy(() => import('./components/PredictionPage').then(m => ({ default: m.PredictionPage })));
const EventResultsPage = lazy(() => import('./components/EventResultsPage').then(m => ({ default: m.EventResultsPage })));
const AccountDetails = lazy(() => import('./components/AccountDetails').then(m => ({ default: m.AccountDetails })));
const LeagueManagement = lazy(() => import('./components/LeagueManagementPage').then(m => ({ default: m.LeagueManagement })));
const LeagueMessages = lazy(() => import('./components/LeagueMessagesPage').then(m => ({ default: m.LeagueMessages })));
const AdminControlPanel = lazy(() => import('./components/AdminControlPanel').then(m => ({ default: m.AdminControlPanel })));
const AdminLivePositions = lazy(() => import('./components/AdminLivePositions').then(m => ({ default: m.AdminLivePositions })));
const AdminRaceResults = lazy(() => import('./components/AdminRaceResults').then(m => ({ default: m.AdminRaceResults })));
const LiveRaceView = lazy(() => import('./components/LiveRaceView').then(m => ({ default: m.LiveRaceView })));
const LiveRacePage = lazy(() => import('./components/LiveRacePage').then(m => ({ default: m.LiveRacePage })));
const TermsOfServicePage = lazy(() => import('./components/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const ContactPage = lazy(() => import('./components/ContactPage').then(m => ({ default: m.ContactPage })));

// Loading component
const LoadingFallback = () => (
  <div className="flex justify-center items-center min-h-[200px]">
    <div className="flex items-center gap-3" role="status" aria-live="polite">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" aria-hidden="true"></div>
      <span className="text-lg text-muted-foreground">Loading...</span>
    </div>
  </div>
);

// Auth pages component
const AuthPages: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isLogin = location.pathname === '/login' || location.pathname === '/';
  const isResetPassword = location.pathname === '/reset-password';

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PublicHeader />
      <div className="flex-1 flex items-center justify-center">
        <main id="main-content" aria-label={isResetPassword ? 'Reset Password' : isLogin ? 'Login' : 'Register'} className="w-full max-w-md mx-auto py-8 px-6">
          <Suspense fallback={<LoadingFallback />}>
            {isResetPassword ? (
              <ResetPasswordForm
                onSwitchToLogin={() => navigate('/login')}
              />
            ) : isLogin ? (
              <LoginForm
                onSwitchToRegister={() => navigate('/register')}
                successMessage={location.state?.successMessage}
              />
            ) : (
              <RegisterForm
                onSuccess={() => navigate('/login', { state: { successMessage: 'Email verified successfully! Please log in to continue.' } })}
                onSwitchToLogin={() => navigate('/login')}
              />
            )}
          </Suspense>
        </main>
      </div>
      <Footer />
    </div>
  );
};

// Main app pages component
const MainPages: React.FC = () => {
  const { user, logout, isAdmin, canCreateLeague } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  const { data: leagues } = useUserLeagues();
  const leagueContextId = useMemo(() => {
    return (
      location.pathname.match(/\/standings\/([^/]+)/)?.[1] ||
      location.pathname.match(/\/leagues\/([^/]+)/)?.[1] ||
      null
    );
  }, [location.pathname]);
  const leagueContextName = useMemo(
    () => leagues?.find(l => l.leagueId === leagueContextId)?.name,
    [leagues, leagueContextId]
  );

  // Get current view from location
  const getCurrentView = () => {
    if (location.pathname.startsWith('/standings')) return 'standings';
    if (/^\/leagues\/[^/]+\/(predict|events)/.test(location.pathname)) return 'standings';
    if (location.pathname === '/create-league') return 'createLeague';
    if (location.pathname === '/join-league') return 'joinLeague';
    if (location.pathname === '/account') return 'account';
    if (location.pathname === '/admin') return 'admin';
    return 'leagues';
  };

  const currentView = getCurrentView();

  // Handle success message from navigation state
  React.useEffect(() => {
    if (location.state?.joinSuccess) {
      setSuccessMessage(`Successfully joined ${location.state.leagueName || 'league'}!`);
      setTimeout(() => setSuccessMessage(''), 5000);
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    setSelectedLeagueId(null);
  };

  const handleCreateLeagueSuccess = (joinCode: string) => {
    setSuccessMessage(`League created! Join code: ${joinCode}`);
    navigate('/leagues');
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const handleJoinLeagueSuccess = () => {
    setSuccessMessage('Successfully joined league!');
    navigate('/leagues');
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const handleSelectLeague = (leagueId: string) => {
    setSelectedLeagueId(leagueId);
    navigate(`/standings/${leagueId}`);
  };

  const handleNavigate = (view: string) => {
    const routes: Record<string, string> = {
      leagues: '/leagues',
      createLeague: '/create-league',
      joinLeague: '/join-league',
      account: '/account',
      admin: '/admin',
      standings: selectedLeagueId ? `/standings/${selectedLeagueId}` : '/leagues',
    };
    navigate(routes[view] || '/leagues');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation
        userEmail={user?.email || ''}
        displayName={user?.displayName}
        onLogout={handleLogout}
        currentView={currentView}
        onNavigate={handleNavigate}
        isAdmin={isAdmin}
        contextTitle={leagueContextName}
      />

      <LeagueTabBar />

      <InstallPrompt />
      <main id="main-content" aria-label="Raceqube Fantasy Sports" className="flex-1 w-full max-w-2xl mx-auto px-6 py-3 space-y-6 pb-20">
        {successMessage && <AlertMessage variant="success">{successMessage}</AlertMessage>}

        <Routes>
          <Route path="/leagues" element={
            <Suspense fallback={<LoadingFallback />}>
              <LeagueList
                onSelectLeague={handleSelectLeague}
                onCreateLeague={() => navigate('/create-league')}
                onJoinLeague={() => navigate('/join-league')}
              />
            </Suspense>
          } />
          <Route path="/standings/:leagueId" element={
            <Suspense fallback={<LoadingFallback />}>
              <LeagueStandings
                leagueId={location.pathname.split('/')[2]}
                onBack={() => {
                  navigate('/leagues');
                  setSelectedLeagueId(null);
                }}
              />
            </Suspense>
          } />
          <Route path="/create-league" element={
            canCreateLeague ? (
              <Suspense fallback={<LoadingFallback />}>
                <CreateLeagueForm
                  onSuccess={handleCreateLeagueSuccess}
                  onCancel={() => navigate('/leagues')}
                />
              </Suspense>
            ) : (
              <Navigate to="/leagues" replace />
            )
          } />
          <Route path="/join-league" element={
            <Suspense fallback={<LoadingFallback />}>
              <JoinLeagueForm
                onSuccess={handleJoinLeagueSuccess}
                onCancel={() => navigate('/leagues')}
              />
            </Suspense>
          } />
          <Route path="/account" element={
            <Suspense fallback={<LoadingFallback />}>
              <AccountDetails
                onBack={() => navigate('/leagues')}
              />
            </Suspense>
          } />
          <Route path="/leagues/:leagueId/events" element={
            <Navigate to={`/leagues/${location.pathname.split('/')[2]}/events/upcoming`} replace />
          } />
          <Route path="/leagues/:leagueId/events/upcoming" element={
            <Suspense fallback={<LoadingFallback />}>
              <UpcomingRacesPage />
            </Suspense>
          } />
          <Route path="/leagues/:leagueId/events/results" element={
            <Suspense fallback={<LoadingFallback />}>
              <RaceResultsPage />
            </Suspense>
          } />
          <Route path="/leagues/:leagueId/events/:eventId/predict" element={
            <Suspense fallback={<LoadingFallback />}>
              <PredictionPage />
            </Suspense>
          } />
          <Route path="/leagues/:leagueId/events/:eventId/results" element={
            <Suspense fallback={<LoadingFallback />}>
              <EventResultsPage />
            </Suspense>
          } />
          <Route path="/leagues/:leagueId/manage" element={
            <Suspense fallback={<LoadingFallback />}>
              <LeagueManagement />
            </Suspense>
          } />
          <Route path="/leagues/:leagueId/messages" element={
            <Suspense fallback={<LoadingFallback />}>
              <LeagueMessages />
            </Suspense>
          } />
          <Route path="/leagues/:leagueId/events/:eventId/live" element={
            <Suspense fallback={<LoadingFallback />}>
              <LiveRacePage />
            </Suspense>
          } />
          {isAdmin && (
            <Route path="/admin" element={
              <Suspense fallback={<LoadingFallback />}>
                <AdminControlPanel />
              </Suspense>
            } />
          )}
          {isAdmin && (
            <Route path="/admin/live-positions" element={
              <Suspense fallback={<LoadingFallback />}>
                <AdminLivePositions />
              </Suspense>
            } />
          )}
          {isAdmin && (
            <Route path="/admin/race-results" element={
              <Suspense fallback={<LoadingFallback />}>
                <AdminRaceResults />
              </Suspense>
            } />
          )}
          {isAdmin && (
            <Route path="/live" element={
              <Suspense fallback={<LoadingFallback />}>
                <LiveRaceView 
                  sessionKey={9999} 
                  leagueId="test" 
                  eventId="test-event" 
                  leagueTypeId="mock"
                  event={{
                    eventId: 'test-event',
                    eventName: 'Test Race',
                    eventDate: Date.now(),
                    deadline: Date.now(),
                    status: 'in_progress',
                    sessionKey: 9999,
                    leagueTypeId: 'mock',
                  }}
                />
              </Suspense>
            } />
          )}
          <Route path="/terms" element={
            <div className="flex-1">
              <Suspense fallback={<LoadingFallback />}>
                <TermsOfServicePage />
              </Suspense>
            </div>
          } />
          <Route path="/privacy" element={
            <div className="flex-1">
              <Suspense fallback={<LoadingFallback />}>
                <PrivacyPolicyPage />
              </Suspense>
            </div>
          } />
          <Route path="/contact" element={
            <div className="flex-1">
              <Suspense fallback={<LoadingFallback />}>
                <ContactPage />
              </Suspense>
            </div>
          } />
          <Route path="*" element={<Navigate to="/leagues" replace />} />
        </Routes>
      </main>
    </div>
  );
};

const AppContent: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex justify-center items-center h-screen">
          <div className="flex items-center gap-3" role="status" aria-live="polite">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" aria-hidden="true"></div>
            <span className="text-lg text-muted-foreground">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Allow invite links to be accessed without authentication
  if (location.pathname.startsWith('/invite/')) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <InviteLinkHandler />
      </Suspense>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<AuthPages />} />
        <Route path="/register" element={<AuthPages />} />
        <Route path="/reset-password" element={<AuthPages />} />
        <Route path="/terms" element={
          <div className="min-h-screen bg-background flex flex-col">
            <PublicHeader />
            <main className="flex-1">
              <Suspense fallback={<LoadingFallback />}>
                <TermsOfServicePage />
              </Suspense>
            </main>
            <Footer />
          </div>
        } />
        <Route path="/privacy" element={
          <div className="min-h-screen bg-background flex flex-col">
            <PublicHeader />
            <main className="flex-1">
              <Suspense fallback={<LoadingFallback />}>
                <PrivacyPolicyPage />
              </Suspense>
            </main>
            <Footer />
          </div>
        } />
        <Route path="/contact" element={
          <div className="min-h-screen bg-background flex flex-col">
            <PublicHeader />
            <main className="flex-1">
              <Suspense fallback={<LoadingFallback />}>
                <ContactPage />
              </Suspense>
            </main>
            <Footer />
          </div>
        } />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return <MainPages />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <OfflineBanner />
          <PWAUpdatePrompt />
          <Routes>
            <Route path="/invite/:token" element={
              <Suspense fallback={<LoadingFallback />}>
                <InviteLinkHandler />
              </Suspense>
            } />
            <Route path="*" element={<AppContent />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
